"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@packages/ui/components/button";
import { cn } from "@packages/ui/lib/utils";
import { AlertTriangle, Home, RefreshCw } from "lucide-react";
interface ErrorPageProps {
  error: Error & {
    digest?: string;
  };
  reset: () => void;
  homeHref?: string;
  className?: string;
}
function ErrorPage(t0) {
  const $ = _c(22);
  const {
    error,
    reset,
    homeHref: t1,
    className
  } = t0;
  const homeHref = t1 === undefined ? "/" : t1;
  let t2;
  if ($[0] !== className) {
    t2 = cn("flex min-h-[400px] flex-col items-center justify-center gap-6 p-8 text-center", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex size-16 items-center justify-center rounded-full bg-destructive/10"><AlertTriangle className="size-8 text-destructive" /></div>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <h1 className="text-2xl font-semibold tracking-tight">Something went wrong</h1>;
    t5 = <p className="text-muted-foreground max-w-md">An unexpected error occurred. Please try again or return to the home page.</p>;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  let t6;
  if ($[5] !== error.digest) {
    t6 = error.digest && <p className="text-muted-foreground text-xs">Error ID: {error.digest}</p>;
    $[5] = error.digest;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t6) {
    t7 = <div className="space-y-2">{t4}{t5}{t6}</div>;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <RefreshCw className="size-4" />;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== reset) {
    t9 = <Button variant="outline" onClick={reset}>{t8}Try again</Button>;
    $[10] = reset;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Home className="size-4" />;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== homeHref) {
    t11 = <Button asChild={true}><a href={homeHref}>{t10}Go home</a></Button>;
    $[13] = homeHref;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t11 || $[16] !== t9) {
    t12 = <div className="flex gap-3">{t9}{t11}</div>;
    $[15] = t11;
    $[16] = t9;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t12 || $[19] !== t2 || $[20] !== t7) {
    t13 = <div className={t2}>{t3}{t7}{t12}</div>;
    $[18] = t12;
    $[19] = t2;
    $[20] = t7;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  return t13;
}
interface GlobalErrorPageProps {
  error: Error & {
    digest?: string;
  };
  reset: () => void;
}
function GlobalErrorPage(t0) {
  const $ = _c(13);
  const {
    error,
    reset
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex size-16 items-center justify-center rounded-full bg-red-100"><AlertTriangle className="size-8 text-red-600" /></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <h1 className="text-2xl font-semibold tracking-tight">Something went wrong</h1>;
    t3 = <p className="max-w-md text-gray-500">A critical error occurred. Please try again.</p>;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] !== error.digest) {
    t4 = error.digest && <p className="text-xs text-gray-400">Error ID: {error.digest}</p>;
    $[3] = error.digest;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t4) {
    t5 = <div className="space-y-2">{t2}{t3}{t4}</div>;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <RefreshCw className="size-4" />;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== reset) {
    t7 = <button type="button" onClick={reset} className="inline-flex items-center gap-2 rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800">{t6}Try again</button>;
    $[8] = reset;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t5 || $[11] !== t7) {
    t8 = <html lang="en"><body><div className="flex min-h-screen flex-col items-center justify-center gap-6 p-8 text-center">{t1}{t5}{t7}</div></body></html>;
    $[10] = t5;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
}
export { ErrorPage, GlobalErrorPage };